// ===========================================
// STAGED CHIPS STORE
// ===========================================
// Manages the staged betting chips state
// Shared between ControlPanel and StagedChipsDisplay
// ===========================================
// NOTE: No persistence - staged bet starts fresh each session.
// Players use autobet if they want to repeat bets.
// ===========================================

import { create } from 'zustand';

interface StagedChipsState {
  /** Total staged amount in dollars */
  stagedAmount: number;
  /** Array of chip denominations staged */
  stagedChips: number[];
  /** Whether clear animation is in progress */
  clearAnimating: boolean;
  /** Whether bet animation is in progress */
  betAnimating: boolean;
  /** Whether autobet is enabled */
  autobetEnabled: boolean;

  // Actions
  setStagedAmount: (amount: number) => void;
  setStagedChips: (chips: number[]) => void;
  setClearAnimating: (animating: boolean) => void;
  setBetAnimating: (animating: boolean) => void;
  setAutobetEnabled: (enabled: boolean) => void;
  toggleAutobet: () => void;

  /** Add a chip to staged (simple add, no consolidation) */
  addChipSimple: (value: number) => void;

  /** Remove one chip of a denomination */
  removeChip: (value: number) => void;

  /** Clear all staged chips */
  clearAll: () => void;

  /** Reset entire state (for new session) */
  reset: () => void;
}

const initialState = {
  stagedAmount: 0,
  stagedChips: [] as number[],
  clearAnimating: false,
  betAnimating: false,
  autobetEnabled: false,
};

export const useStagedChipsStore = create<StagedChipsState>()((set) => ({
  ...initialState,

  setStagedAmount: (amount) => set({ stagedAmount: amount }),

  setStagedChips: (chips) => set({ stagedChips: chips }),

  setClearAnimating: (animating) => set({ clearAnimating: animating }),

  setBetAnimating: (animating) => set({ betAnimating: animating }),

  setAutobetEnabled: (enabled) => set({ autobetEnabled: enabled }),

  toggleAutobet: () => set((state) => ({ autobetEnabled: !state.autobetEnabled })),

  addChipSimple: (value) => set((state) => ({
    stagedAmount: state.stagedAmount + value,
    stagedChips: [...state.stagedChips, value],
  })),

  removeChip: (value) => set((state) => {
    const idx = state.stagedChips.indexOf(value);
    if (idx === -1) return state;
    const newChips = [...state.stagedChips];
    newChips.splice(idx, 1);
    return {
      stagedAmount: state.stagedAmount - value,
      stagedChips: newChips,
    };
  }),

  clearAll: () => set({ stagedAmount: 0, stagedChips: [] }),

  reset: () => set({ ...initialState }),
}));

// Selectors for common access patterns
export const useStagedAmount = () => useStagedChipsStore((state) => state.stagedAmount);
export const useStagedChips = () => useStagedChipsStore((state) => state.stagedChips);
export const useClearAnimating = () => useStagedChipsStore((state) => state.clearAnimating);
export const useBetAnimating = () => useStagedChipsStore((state) => state.betAnimating);
export const useAutobetEnabled = () => useStagedChipsStore((state) => state.autobetEnabled);
